import React, { useEffect, useState } from "react";
import { Plus, Trash2, CalendarRange } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Shell, Empty } from "@/components/Shell";
import { Select } from "@/pages/Projects";
import { StatusPill } from "@/components/LogSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const nextSunday = () => {
  const d = new Date();
  d.setDate(d.getDate() + ((7 - d.getDay()) % 7 || 7));
  return d.toISOString().slice(0, 10);
};

export default function AdminPeriods() {
  const [periods, setPeriods] = useState([]);
  const [gen, setGen] = useState({ first_period_ending: nextSunday(), weeks: 12 });
  const [manual, setManual] = useState({ name: "", period_ending: "" });

  const load = () => api.get("/reporting-periods").then(({ data }) => setPeriods(data));
  useEffect(() => { load(); }, []);

  const generate = async () => {
    try {
      const { data } = await api.post("/reporting-periods/generate", {
        first_period_ending: gen.first_period_ending,
        weeks: Number(gen.weeks) || 12,
      });
      toast.success(`${data.created} reporting period${data.created === 1 ? "" : "s"} created`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const addManual = async () => {
    if (!manual.period_ending) return toast.error("Period ending date is required");
    try {
      await api.post("/reporting-periods", {
        name: manual.name?.trim() || `Week ending ${manual.period_ending}`,
        period_ending: manual.period_ending,
      });
      setManual({ name: "", period_ending: "" });
      toast.success("Reporting period added");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const setStatus = async (p, status) => {
    try {
      await api.put(`/reporting-periods/${p.id}`, { ...p, status });
      load();
      toast.success(status === "Closed" ? "Period closed for new updates" : "Period reopened");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/reporting-periods/${p.id}`);
      load();
      toast.success("Deleted");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <Shell
      testId="admin-periods-page"
      title="Reporting Periods"
      subtitle="Weekly periods that project managers select when submitting a status update"
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-5">
          <div className="panel p-5" data-testid="generate-periods-panel">
            <h3 className="font-display text-base font-semibold text-slate-800">Generate Weekly Periods</h3>
            <p className="mt-1 text-xs text-slate-500">Creates one period per week. Existing dates are skipped.</p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="field-label">First Period Ending</label>
                <Input data-testid="gen-first-input" type="date" value={gen.first_period_ending} onChange={(e) => setGen({ ...gen, first_period_ending: e.target.value })} />
              </div>
              <div>
                <label className="field-label">Number of Weeks</label>
                <Input data-testid="gen-weeks-input" type="number" min="1" max="104" value={gen.weeks} onChange={(e) => setGen({ ...gen, weeks: e.target.value })} />
              </div>
              <Button data-testid="generate-periods-btn" onClick={generate} className="w-full gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}>
                <CalendarRange size={14} /> Generate
              </Button>
            </div>
          </div>

          <div className="panel p-5" data-testid="add-period-panel">
            <h3 className="font-display text-base font-semibold text-slate-800">Add a Single Period</h3>
            <div className="mt-4 space-y-3">
              <div>
                <label className="field-label">Period Ending</label>
                <Input data-testid="period-date-input" type="date" value={manual.period_ending} onChange={(e) => setManual({ ...manual, period_ending: e.target.value })} />
              </div>
              <div>
                <label className="field-label">Name (optional)</label>
                <Input data-testid="period-name-input" value={manual.name} onChange={(e) => setManual({ ...manual, name: e.target.value })} placeholder="e.g. P1 Week 3" />
              </div>
              <Button data-testid="add-period-btn" onClick={addManual} variant="outline" className="w-full gap-1.5"><Plus size={14} /> Add Period</Button>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          {periods.length === 0 ? (
            <Empty testId="periods-empty" text="No reporting periods yet. Generate a set to get started." />
          ) : (
            <div className="panel overflow-x-auto" data-testid="periods-table">
              <table className="w-full min-w-[620px] text-sm">
                <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>{["Name", "Starts", "Period Ending", "Status", ""].map((h) => <th key={h} className="px-4 py-2.5 font-semibold">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {periods.map((p) => (
                    <tr key={p.id} data-testid={`period-row-${p.id}`}>
                      <td className="px-4 py-3 font-medium text-slate-800">{p.name}</td>
                      <td className="px-4 py-3 text-slate-600">{p.start_date || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{p.period_ending}</td>
                      <td className="px-4 py-3"><StatusPill value={p.status} /></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-28">
                            <Select testId={`period-status-${p.id}`} value={p.status} onChange={(v) => setStatus(p, v)} options={["Open", "Closed"]} placeholder="Status" />
                          </div>
                          <button data-testid={`delete-period-${p.id}`} onClick={() => remove(p)} aria-label="Delete period" title="Delete period" className="text-slate-400 transition-colors hover:text-red-600"><Trash2 size={15} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}
